import React, { useState, useEffect, useCallback } from 'react';

const API = '/api';

function authHeaders() {
  const token = localStorage.getItem('lc_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' };
}

// ─── Design tokens ──────────────────────────────────────────────────────────
// A factory-floor telemetry identity: tabular monospace for every number (the
// signature — reads like precision instrumentation, not a generic dashboard),
// a refined sans for labels, soft elevation instead of flat borders, and the
// same green/amber/red/blue semantics staff already rely on, just deepened.
const FONT_UI = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
const FONT_MONO = "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace";

const C = {
  ink: '#14171C', steel: '#4B5563', mute: '#8B93A1',
  grn: '#2F6B1F', grnBg: '#E9F3E2', grnL: '#5FA83A', grnDeep: '#1F4A15',
  amb: '#92600A', ambBg: '#FBF0DC',
  red: '#AB2F2F', redBg: '#FCEAEA',
  acc: '#0F4C87', accBg: '#E8F1FA', accDeep: '#0A3A69',
  hairline: 'rgba(20,23,28,0.09)',
  bg: '#EDEAE2', card: '#FFFFFF',
  txt: '#14171C', txt2: '#5B6472',
  shadow: '0 1px 2px rgba(20,23,28,0.04), 0 6px 20px rgba(20,23,28,0.05)',
  shadowSm: '0 1px 2px rgba(20,23,28,0.05)',
};

function effColor(pct) {
  if (pct >= 75) return C.grn;
  if (pct >= 60) return C.amb;
  return C.red;
}

function skillBg(pct) {
  if (pct >= 80) return { bg: C.grnBg, color: C.grn };
  if (pct >= 60) return { bg: C.ambBg, color: C.amb };
  return { bg: C.redBg, color: C.red };
}

// One-off font import — JetBrains Mono for all numeric/data display.
function FontLoad() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Inter:wght@400;500;600;700;800&display=swap');
    `}</style>
  );
}

// ─── Operation output bar chart (the "bottleneck" graph) ─────────────────────
function OperationOutputChart({ operations }) {
  if (!operations || operations.length === 0) return null;

  const maxOutput = Math.max(...operations.map(op => op.total_output || 0), 1);
  const yMax = Math.ceil((maxOutput * 1.2) / 20) * 20;
  const yTicks = [];
  for (let v = 0; v <= yMax; v += Math.ceil(yMax / 5 / 20) * 20) yTicks.push(v);

  const BAR_W = 46;
  const BAR_GAP = 16;
  const CHART_H = 230;
  const LABEL_H = 56;
  const Y_LABEL_W = 34;
  const totalW = operations.length * (BAR_W + BAR_GAP);

  return (
    <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden' }}>
      <div style={{ display: 'flex', minWidth: totalW + Y_LABEL_W + 20 }}>
        {/* Y axis */}
        <div style={{ width: Y_LABEL_W, flexShrink: 0, position: 'relative', height: CHART_H + LABEL_H }}>
          {yTicks.slice().reverse().map((v, i) => (
            <div key={v} style={{
              position: 'absolute', right: 8,
              top: (i / (yTicks.length - 1)) * CHART_H - 6,
              fontSize: 10, fontFamily: FONT_MONO, color: C.mute, textAlign: 'right'
            }}>{v}</div>
          ))}
        </div>

        {/* Chart area */}
        <div style={{ flex: 1, position: 'relative' }}>
          {/* Grid lines */}
          {yTicks.map((v, i) => (
            <div key={v} style={{
              position: 'absolute', left: 0, right: 0,
              top: ((yMax - v) / yMax) * CHART_H,
              borderTop: `0.5px solid ${i === 0 ? 'rgba(20,23,28,0.16)' : 'rgba(20,23,28,0.06)'}`
            }} />
          ))}

          {/* Bars + labels */}
          <div style={{ display: 'flex', alignItems: 'flex-end', height: CHART_H, gap: BAR_GAP, paddingBottom: 0 }}>
            {operations.map((op, idx) => {
              const output = op.total_output || 0;
              const barH = yMax > 0 ? Math.max((output / yMax) * CHART_H, output > 0 ? 2 : 0) : 0;
              const isBottleneck = op.is_actual_bottleneck !== undefined ? op.is_actual_bottleneck : op.is_bottleneck;
              const barGradient = isBottleneck
                ? `linear-gradient(180deg, ${C.red} 0%, #7A1F1F 100%)`
                : `linear-gradient(180deg, ${C.acc} 0%, ${C.accDeep} 100%)`;
              return (
                <div key={op.id || idx} style={{ width: BAR_W, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* Output value above bar */}
                  <div style={{
                    fontSize: 11, fontWeight: 700, fontFamily: FONT_MONO,
                    color: isBottleneck ? C.red : C.accDeep, marginBottom: 4, minHeight: 14
                  }}>
                    {output > 0 ? output : ''}
                  </div>
                  {/* Bar */}
                  <div style={{
                    width: BAR_W, height: barH, background: barGradient,
                    borderRadius: '4px 4px 2px 2px', transition: 'height 0.3s ease',
                    boxShadow: isBottleneck ? '0 2px 8px rgba(171,47,47,0.25)' : '0 2px 8px rgba(15,76,135,0.18)'
                  }} />
                </div>
              );
            })}
          </div>

          {/* X axis line */}
          <div style={{ borderTop: `1.5px solid rgba(20,23,28,0.16)` }} />

          {/* X labels */}
          <div style={{ display: 'flex', gap: BAR_GAP, height: LABEL_H, alignItems: 'flex-start', paddingTop: 6 }}>
            {operations.map((op, idx) => {
              const label = op.operation_name ? op.operation_name.toUpperCase() : '';
              const words = label.split(' ');
              const isBottleneck = op.is_actual_bottleneck !== undefined ? op.is_actual_bottleneck : op.is_bottleneck;
              return (
                <div key={op.id || idx} style={{
                  width: BAR_W, flexShrink: 0, fontSize: 8.5, fontWeight: isBottleneck ? 700 : 500,
                  color: isBottleneck ? C.red : C.steel, fontFamily: FONT_UI,
                  textAlign: 'center', lineHeight: 1.35, wordBreak: 'break-word',
                  overflow: 'hidden', maxHeight: LABEL_H - 4, letterSpacing: 0.1
                }}>
                  {words.map((w, wi) => <div key={wi}>{w}</div>)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Hourly production table (Style / Buyer / Target / HR1-8 / NPH / Total) ──
function HourlyTable({ dep, targetPerHr, hourlyData, nph, cumulativeTotal }) {
  const cellBase = {
    padding: '14px 10px', textAlign: 'center', fontFamily: FONT_MONO,
    fontSize: 15, fontWeight: 600, borderRight: `0.5px solid ${C.hairline}`,
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
        <thead>
          <tr style={{ background: 'linear-gradient(180deg, #FAF9F6 0%, #F3F1EA 100%)' }}>
            {['Style No', 'Buyer', 'Target/Hr'].map(h => (
              <th key={h} style={{
                padding: '11px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                letterSpacing: 0.6, textTransform: 'uppercase', color: C.txt2,
                borderBottom: `1.5px solid ${C.hairline}`, borderRight: `0.5px solid ${C.hairline}`,
                fontFamily: FONT_UI, whiteSpace: 'nowrap'
              }}>{h}</th>
            ))}
            {hourlyData.map(h => (
              <th key={h.hour} style={{
                padding: '11px 8px', textAlign: 'center', fontSize: 10, fontWeight: 700,
                letterSpacing: 0.6, color: h.is_current ? C.acc : C.txt2,
                background: h.is_current ? C.accBg : 'transparent',
                borderBottom: `1.5px solid ${C.hairline}`, borderRight: `0.5px solid ${C.hairline}`,
                fontFamily: FONT_UI, whiteSpace: 'nowrap'
              }}>
                {h.label}{h.is_current ? ' ●' : ''}
              </th>
            ))}
            <th style={{
              padding: '11px 10px', textAlign: 'center', fontSize: 10, fontWeight: 700,
              letterSpacing: 0.6, textTransform: 'uppercase', color: C.txt2,
              borderBottom: `1.5px solid ${C.hairline}`, borderRight: `0.5px solid ${C.hairline}`,
              fontFamily: FONT_UI, whiteSpace: 'nowrap'
            }}>NPH</th>
            <th style={{
              padding: '11px 14px', textAlign: 'center', fontSize: 10, fontWeight: 700,
              letterSpacing: 0.6, textTransform: 'uppercase', color: C.accDeep,
              borderBottom: `1.5px solid ${C.hairline}`,
              fontFamily: FONT_UI, whiteSpace: 'nowrap'
            }}>Cumulative Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{
              padding: '14px', fontSize: 14, fontWeight: 700, color: C.txt,
              fontFamily: FONT_UI, borderRight: `0.5px solid ${C.hairline}`, whiteSpace: 'nowrap'
            }}>{dep.style_number || '—'}</td>
            <td style={{
              padding: '14px', fontSize: 13, fontWeight: 500, color: C.steel,
              fontFamily: FONT_UI, borderRight: `0.5px solid ${C.hairline}`, whiteSpace: 'nowrap'
            }}>{dep.buyer || '—'}</td>
            <td style={{ ...cellBase, color: C.txt, background: '#FAFAF8' }}>{targetPerHr}</td>
            {hourlyData.map(h => {
              const hasValue = h.actual !== null && h.actual !== undefined;
              const met = hasValue && h.actual >= (h.target || targetPerHr);
              return (
                <td key={h.hour} style={{
                  ...cellBase,
                  background: h.is_current ? C.accBg : hasValue ? (met ? C.grnBg : C.redBg) : 'transparent',
                  color: hasValue ? (met ? C.grn : C.red) : C.mute,
                }}>
                  {hasValue ? h.actual : '—'}
                </td>
              );
            })}
            <td style={{ ...cellBase, color: nph?.output ? C.amb : C.mute, background: nph?.output ? C.ambBg : 'transparent' }}>
              {nph?.output || '—'}
            </td>
            <td style={{
              padding: '10px 14px', textAlign: 'center'
            }}>
              <span style={{
                display: 'inline-block', padding: '6px 18px', borderRadius: 8,
                background: `linear-gradient(135deg, ${C.acc} 0%, ${C.accDeep} 100%)`,
                color: '#fff', fontFamily: FONT_MONO, fontSize: 17, fontWeight: 700,
                boxShadow: '0 2px 10px rgba(15,76,135,0.28)'
              }}>{cumulativeTotal}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ─── KPI Tile ─────────────────────────────────────────────────────────────────
function KpiTile({ label, value, sub, color, bg }) {
  const accent = color || C.acc;
  return (
    <div style={{
      background: bg || C.card, borderRadius: 12, padding: '14px 16px 13px',
      boxShadow: C.shadow, border: `0.5px solid ${C.hairline}`, flex: 1, minWidth: 0,
      position: 'relative', overflow: 'hidden'
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: accent, opacity: 0.85 }} />
      <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, fontFamily: FONT_UI }}>{label}</div>
      <div style={{ fontSize: 27, fontWeight: 700, color: accent, lineHeight: 1, fontFamily: FONT_MONO, letterSpacing: -0.5 }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: C.txt2, marginTop: 5, fontFamily: FONT_UI }}>{sub}</div>}
    </div>
  );
}

// ─── Operation row ────────────────────────────────────────────────────────────
function OperationRow({ op }) {
  const sc = skillBg(op.avg_skill);
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '2fr 60px 60px 60px 80px 1fr',
      alignItems: 'center', gap: 8,
      padding: '5px 10px',
      background: op.is_bottleneck ? '#FFF5F5' : op.has_gap ? '#FFFBEB' : '#fff',
      borderBottom: `0.5px solid ${C.hairline}`,
      borderLeft: `3px solid ${op.is_bottleneck ? C.red : op.has_gap ? C.amb : 'transparent'}`
    }}>
      <div style={{ fontSize: 11, fontWeight: op.is_bottleneck ? 700 : 500, color: C.txt }}>
        {op.is_bottleneck && <span style={{ color: C.red, marginRight: 4 }}>⚠</span>}
        {op.operation_name}
      </div>
      <div style={{ fontSize: 10, color: C.txt2, textAlign: 'center' }}>
        {parseFloat(op.sam).toFixed(3)}
      </div>
      <div style={{ fontSize: 10, textAlign: 'center', color: C.txt2 }}>
        {op.deployed_count}/{op.operators_count}
      </div>
      <div style={{ fontSize: 10, textAlign: 'center', color: C.txt2 }}>
        {op.skill_req_pct}%
      </div>
      <div style={{
        fontSize: 10, fontWeight: 700, textAlign: 'center',
        padding: '2px 6px', borderRadius: 4,
        background: sc.bg, color: sc.color
      }}>
        {op.avg_skill ? `${op.avg_skill}%` : '—'}
      </div>
      {/* Operator chips */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {op.operators.map((s, i) => {
          const osc = skillBg(parseFloat(s.skill_pct));
          return (
            <div key={i} style={{
              fontSize: 9, padding: '1px 5px', borderRadius: 3,
              background: osc.bg, color: osc.color, fontWeight: 600,
              whiteSpace: 'nowrap'
            }}>
              {s.emp_name?.split(' ')[0]} {Math.round(parseFloat(s.skill_pct))}%
            </div>
          );
        })}
        {Array.from({ length: op.operators_count - op.deployed_count }).map((_, i) => (
          <div key={`vacant-${i}`} style={{
            fontSize: 9, padding: '1px 5px', borderRadius: 3,
            background: C.redBg, color: C.red, fontWeight: 600
          }}>Vacant</div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function SupervisorDashboard() {
  const [deployments, setDeployments] = useState([]);
  const [shiftFilter, setShiftFilter] = useState('');
  const [depId, setDepId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    const el = document.getElementById('lc-page-title');
    if (el) el.textContent = 'Supervisor dashboard';
  }, []);

  // Load active deployments
  useEffect(() => {
    fetch(`${API}/supervisor-dashboard`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : [];
        setDeployments(list);
        if (list.length === 1 && !depId) setDepId(String(list[0].id));
      })
      .catch(() => {});
  }, []);

  const loadData = useCallback((id) => {
    if (!id) return;
    setLoading(true);
    fetch(`${API}/supervisor-dashboard/${id}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        if (!d.error) { setData(d); setLastRefresh(new Date()); }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { if (depId) loadData(depId); }, [depId, loadData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (!depId) return;
    const interval = setInterval(() => loadData(depId), 60000);
    return () => clearInterval(interval);
  }, [depId, loadData]);

  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  const dateStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div style={{
      background: `radial-gradient(ellipse 1200px 400px at 50% -10%, rgba(15,76,135,0.05), transparent), ${C.bg}`,
      minHeight: '100vh', padding: 18,
      fontFamily: FONT_UI
    }}>
      <FontLoad />

      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14, paddingBottom: 14, borderBottom: `1.5px solid ${C.hairline}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.ink, letterSpacing: -0.3 }}>Skillflo</div>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: C.mute, letterSpacing: 0.3, textTransform: 'uppercase' }}>Supervisor Dashboard</div>
          </div>
          {data && (
            <>
              <div style={{ width: 1, height: 32, background: C.hairline, margin: '0 4px' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
                  Line {data.dep.line_number} · {data.dep.style_number}
                </div>
                <div style={{ fontSize: 11, color: C.txt2 }}>
                  {data.dep.buyer} · {data.dep.shift_name} shift
                </div>
              </div>
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Shift filter */}
          <select value={shiftFilter} onChange={e => setShiftFilter(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 7, border: `0.5px solid ${C.hairline}`, fontSize: 12, background: C.card, boxShadow: C.shadowSm, fontFamily: FONT_UI, color: C.txt }}>
            <option value="">All shifts</option>
            {[...new Set(deployments.map(d => d.shift_name).filter(Boolean))].sort().map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {/* Line selector */}
          <select value={depId} onChange={e => setDepId(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 7, border: `0.5px solid ${C.hairline}`, fontSize: 12, background: C.card, boxShadow: C.shadowSm, fontFamily: FONT_UI, color: C.txt }}>
            <option value="">Select line…</option>
            {deployments.filter(d => !shiftFilter || d.shift_name === shiftFilter).map(d => (
              <option key={d.id} value={d.id}>Line {d.line_number} · {d.style_number}</option>
            ))}
          </select>
          <div style={{ fontSize: 12, color: C.txt2, textAlign: 'right' }}>
            {dateStr}<br/><span style={{ fontFamily: FONT_MONO, fontWeight: 700, color: C.ink, fontSize: 14 }}>{timeStr}</span>
          </div>
          {lastRefresh && (
            <div style={{ fontSize: 9.5, color: C.mute, fontFamily: FONT_MONO }}>
              ↺ {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
            </div>
          )}
          <button onClick={() => loadData(depId)} style={{
            padding: '6px 12px', borderRadius: 7, border: `0.5px solid ${C.hairline}`,
            background: C.card, fontSize: 11, cursor: 'pointer', color: C.acc, fontWeight: 700, boxShadow: C.shadowSm
          }}>↺ Refresh</button>
        </div>
      </div>

      {!data && !loading && (
        <div style={{ textAlign: 'center', padding: 60, color: C.txt2, fontSize: 14 }}>
          {deployments.length === 0
            ? 'No active deployments. Activate a deployment first.'
            : 'Select an active line to view the dashboard.'}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: C.txt2, fontSize: 13 }}>Loading…</div>
      )}

      {data && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* TOP — KPI tiles */}
          <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
            <KpiTile
              label="DB Efficiency"
              value={`${data.db_efficiency}%`}
              sub="Deployment plan"
              color={effColor(data.db_efficiency)}
            />
            <KpiTile
              label="Running Eff."
              value={`${data.running_efficiency}%`}
              sub={`H${data.current_hour} now`}
              color={effColor(data.running_efficiency)}
            />
            <KpiTile label="Output till now"
              value={(data.total_output || 0) + (data.nph?.output || 0)}
              sub={`Target: ${data.target_by_now}${data.nph?.output ? ` · NPH: ${data.nph.output}` : ''}`}
              color={((data.total_output||0)+(data.nph?.output||0)) >= data.target_by_now ? C.grn : C.red} />
            <KpiTile label="Target / day" value={data.target_per_day} sub={`${data.target_per_hr}/hr`} />
            <KpiTile label="Deployed" value={`${data.total_deployed}/${data.total_planned}`} color={C.acc} />
            <KpiTile label="Skill gaps" value={data.skill_gaps} color={data.skill_gaps > 0 ? C.amb : C.grn} />
            <KpiTile label="Downtime (min)" value={data.total_dt_minutes || 0} sub={`${(data.active_downtimes || []).filter(d => d.status !== 'Resolved').length} active`} color={(data.total_dt_minutes || 0) > 0 ? C.red : C.grn} />
          </div>

          {/* Downtime alert banner — compact, full width, only if active */}
          {data.active_downtimes.length > 0 && (
            <div style={{
              background: `linear-gradient(135deg, ${C.redBg} 0%, #FDF0F0 100%)`, borderRadius: 12,
              padding: '10px 16px', border: `1px solid rgba(171,47,47,0.25)`, boxShadow: C.shadowSm,
              flexShrink: 0, display: 'flex', alignItems: 'center', gap: 14, overflowX: 'auto'
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.red, whiteSpace: 'nowrap', letterSpacing: 0.3 }}>
                ⚠ DOWNTIME ({data.active_downtimes.length})
              </div>
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                {data.active_downtimes.map(dt => (
                  <div key={dt.id} style={{
                    background: '#fff', borderRadius: 8, padding: '6px 12px',
                    border: `0.5px solid ${C.hairline}`, display: 'flex', alignItems: 'center', gap: 8,
                    whiteSpace: 'nowrap', flexShrink: 0
                  }}>
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: dt.status === 'running' ? C.red : C.amb, flexShrink: 0
                    }} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.ink }}>{dt.operation_name}</span>
                    <span style={{ fontSize: 10.5, color: C.txt2 }}>{dt.emp_name} · {dt.reason_name}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, fontFamily: FONT_MONO, color: dt.status === 'running' ? C.red : C.amb }}>
                      {Math.round(dt.duration_mins || 0)}m
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hourly production table */}
          <div style={{
            background: C.card, borderRadius: 12, boxShadow: C.shadow,
            border: `0.5px solid ${C.hairline}`, overflow: 'hidden', flexShrink: 0
          }}>
            <div style={{
              padding: '13px 18px', borderBottom: `1.5px solid ${C.hairline}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: C.ink, letterSpacing: 0.1 }}>
                Hourly Output — Target vs Actual
              </div>
              <div style={{ display: 'flex', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 9, height: 9, borderRadius: 3, background: C.grn }} />
                  <span style={{ fontSize: 9.5, color: C.txt2, fontWeight: 600 }}>Met target</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 9, height: 9, borderRadius: 3, background: C.red }} />
                  <span style={{ fontSize: 9.5, color: C.txt2, fontWeight: 600 }}>Below target</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 9, height: 9, borderRadius: 3, background: C.acc }} />
                  <span style={{ fontSize: 9.5, color: C.txt2, fontWeight: 600 }}>Current hour</span>
                </div>
              </div>
            </div>
            <HourlyTable
              dep={data.dep}
              targetPerHr={data.target_per_hr}
              hourlyData={data.hourly_data}
              nph={data.nph}
              cumulativeTotal={(data.total_output || 0) + (data.nph?.output || 0)}
            />
          </div>

          {/* Bottleneck operation output chart */}
          <div style={{
            background: C.card, borderRadius: 12, boxShadow: C.shadow,
            border: `0.5px solid ${C.hairline}`, overflow: 'hidden',
            display: 'flex', flexDirection: 'column', padding: '16px 20px',
            minHeight: 400, flexShrink: 0
          }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: C.ink, marginBottom: 16, letterSpacing: 0.1 }}>
              Output per Operation — Bottleneck Analysis (total till now)
            </div>
            <OperationOutputChart operations={data.operations} />
            <div style={{
              display: 'flex', gap: 18, justifyContent: 'center', marginTop: 12,
              paddingTop: 10, borderTop: `0.5px solid ${C.hairline}`, flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 10, height: 10, background: C.acc, borderRadius: 3 }} />
                <span style={{ fontSize: 9.5, color: C.txt2, fontWeight: 600 }}>Output</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 10, height: 10, background: C.red, borderRadius: 3 }} />
                <span style={{ fontSize: 9.5, color: C.txt2, fontWeight: 600 }}>Bottleneck</span>
              </div>
              <div style={{ fontSize: 9.5, color: C.mute, marginLeft: 'auto', fontFamily: FONT_MONO }}>
                Auto-refresh every 60s
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}